import type {
  RecordWTimestamp,
  ScatterPlotView,
  LinePlotView,
} from "types/dashboard.types"

export const getXYPlotBounds = (
  view: LinePlotView | ScatterPlotView,
  data: RecordWTimestamp[],
  fromDate: number,
  toDate: number,
): number[][] => {
  const properties = view.properties

  const xKey = properties.axes.x.key || "_time"
  const yKey = properties.axes.y.key || "_value"
  const [xBoundMin, xBoundMax] = properties.axes.x.bounds || []
  const [yBoundMin, yBoundMax] = properties.axes.y.bounds || []
  const parsedXBoundMin = xBoundMin ? Number.parseFloat(xBoundMin) : undefined
  const parsedXBoundMax = xBoundMax ? Number.parseFloat(xBoundMax) : undefined
  const parsedYBoundMin = yBoundMin ? Number.parseFloat(yBoundMin) : undefined
  const parsedYBoundMax = yBoundMax ? Number.parseFloat(yBoundMax) : undefined

  if (data.length === 0) {
    const xMin =
      xKey === "_time" && Number.isFinite(fromDate) ? fromDate : (parsedXBoundMin ?? 0)
    const xMax =
      xKey === "_time" && Number.isFinite(toDate) ? toDate : (parsedXBoundMax ?? xMin + 1)
    const yMin = parsedYBoundMin ?? 0
    const yMax =
      parsedYBoundMax ?? (yMin === 0 ? 1 : yMin + Math.max(0.1, Math.abs(yMin) * 0.05))

    return [
      [xMin, xMax > xMin ? xMax : xMin + 1],
      [yMin, yMax > yMin ? yMax : yMin + 1],
    ]
  }

  let highestDataValue = Number.NEGATIVE_INFINITY
  let lowestDataValue = Number.POSITIVE_INFINITY
  let [xMin, xMax] = [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]

  for (const obj of data) {
    const yValue = obj[yKey] as number
    if (yValue < lowestDataValue) {
      lowestDataValue = yValue
    }
    if (yValue > highestDataValue) {
      highestDataValue = yValue
    }
    if (xKey === "_time") {
      const start = obj._start as number | string | undefined
      if (xMin === Number.POSITIVE_INFINITY && start) {
        xMin = new Date(start).getTime()
      } else if (start === undefined) {
        const xValue = obj[xKey]
        if (xValue !== undefined) {
          const time = new Date(xValue).getTime()
          if (time < xMin) {
            xMin = time
          }
        }
      }
    } else {
      const xValue = obj[xKey] as number
      if (xValue < xMin) {
        xMin = xValue
      }
    }
    if (xKey === "_time") {
      const stop = obj._stop as number | string | undefined
      if (xMax === Number.NEGATIVE_INFINITY && stop) {
        xMax = new Date(stop).getTime()
      } else if (stop === undefined) {
        const xValue = obj[xKey]
        if (xValue !== undefined) {
          const time = new Date(xValue).getTime()
          if (time > xMax) {
            xMax = time
          }
        }
      }
    } else {
      const xValue = obj[xKey] as number
      if (xValue > xMax) {
        xMax = xValue
      }
    }
  }

  xMin = parsedXBoundMin !== undefined && parsedXBoundMin < xMin ? parsedXBoundMin : xMin
  xMax = parsedXBoundMax !== undefined && parsedXBoundMax > xMax ? parsedXBoundMax : xMax

  if (xKey === "_time") {
    if (Number.isFinite(fromDate)) {
      xMin = fromDate
    }
    if (Number.isFinite(toDate)) {
      xMax = toDate
    }
  }

  let yMin =
    parsedYBoundMin !== undefined && parsedYBoundMin <= lowestDataValue
      ? parsedYBoundMin
      : lowestDataValue

  let yMax =
    parsedYBoundMax !== undefined && parsedYBoundMax >= highestDataValue
      ? parsedYBoundMax
      : highestDataValue

  const yRange = yMax - yMin

  if (yRange === 0) {
    const minPadding = Math.max(0.1, Math.abs(yMax) * 0.05)
    yMin -= minPadding
    yMax += minPadding
  } else {
    yMin -= 0.05 * yRange
    yMax += 0.05 * yRange
  }

  return [
    [xMin, xMax],
    [yMin, yMax],
  ]
}
